<template lang="pug">
b-form-group(:label="hideLabel ? null : 'Rut'" label-for="rut")

  // Campo de autocompletado cuando no hay cliente seleccionado
  div(v-if="!clientSelected")
    b-input-group
      b-input-group-prepend
        b-input-group-text.bg-white.border-right-0
          b-icon(icon="search" style="color: #525252 !important;")
      vue-typeahead-bootstrap(
        v-model="rutQuery"
        :data="customers"
        :serializer="serializer"
        :placeholder="placeholder || 'Ej: 12345678-9'"
        @input="handleInput"
        @hit="onRutSelected"
        id="rut-autocomplete-ov"
        :class="{ 'is-invalid': showValidation }"
        class="flex-grow-1"
      )
    .mt-2(v-if="rutQuery && rutQuery.length >= 4 && customers.length === 0 && !searching")
      small.text-muted No se encontraron clientes.
      b-button(
        size="sm"
        variant="link"
        @click="createNewClient"
        class="p-0 text-primary"
      ) Crear nuevo cliente

  // Campo de solo lectura cuando hay cliente seleccionado
  div(v-else)
    .d-flex.align-items-center.justify-content-between.selected-client-wrapper
      span.selected-client {{ rutQuery }}
      b-button(
        size="sm"
        variant="outline-secondary"
        @click="clearClient"
      ) Cambiar

  // Indicador de carga
  div(v-if="searching")
    small.text-muted
      b-spinner(small)
      |  Buscando...

  // Validación
  b-form-invalid-feedback(v-if="showValidation")
    | El Rut es obligatorio.
</template>

<script>
import VueTypeaheadBootstrap from 'vue-typeahead-bootstrap'
import axios from '@/plugins/axios'

export default {
  name: 'RutAutocompleteOV',
  components: {
    VueTypeaheadBootstrap,
  },
  props: {
    value: {
      type: String,
      default: '',
    },
    showValidation: {
      type: Boolean,
      default: false,
    },
    hideLabel: {
      type: Boolean,
      default: false,
    },
    placeholder: {
      type: String,
      default: 'Ej: 12345678-9',
    },
  },
  data() {
    return {
      rutQuery: this.value || '',
      customers: [],
      clientSelected: false,
      searching: false,
      selectedCustomer: null,
      searchTimeout: null,
    }
  },
  watch: {
    value(newVal) {
      this.rutQuery = newVal || ''
    },
  },
  methods: {
    // Maneja el input del campo
    handleInput(value) {
      this.rutQuery = value
      this.clientSelected = false
      this.$emit('input', value)

      // Limpiar timeout anterior
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout)
      }

      // Crear nuevo timeout para búsqueda
      this.searchTimeout = setTimeout(() => {
        this.searchClients(value)
      }, 500)
    },

    // Serializer para mostrar los resultados en el dropdown
    serializer(cliente) {
      if (cliente.tipo_cliente === 'Persona' && cliente.persona) {
        return `${cliente.persona.rut} - ${cliente.persona.nombre} ${cliente.persona.apellido}`
      }
      if (cliente.tipo_cliente === 'Empresa' && cliente.empresa) {
        return `${cliente.empresa.rut} - ${cliente.empresa.razon_social}`
      }
      return cliente.rut || ''
    },

    // Búsqueda de clientes
    async searchClients(rutValue) {
      if (!rutValue || rutValue.length < 4) {
        this.customers = []
        return
      }

      this.searching = true
      try {
        const response = await this.searchClientsByRut(rutValue)
        this.customers = response || []
      } catch (error) {
        console.error('Error buscando clientes OV:', error)
        this.customers = []
      } finally {
        this.searching = false
      }
    },

    // Cuando se selecciona un cliente existente
    onRutSelected(client) {
      // El RUT ingresado corresponde a una empresa: no se puede tratar como
      // Persona. Se delega al padre para que prellene los datos de empresa
      // (ver stepCliente.onEmpresaDetectedEnPersona / Step1Cliente.onEmpresaDetected).
      if (client.tipo_cliente === 'Empresa' && client.empresa) {
        this.selectedCustomer = client
        this.clientSelected = true
        this.rutQuery = client.empresa.rut
        this.$emit('input', this.rutQuery)
        this.$emit('empresa-detected', client.empresa)
        return
      }

      this.selectedCustomer = client
      this.clientSelected = true

      let clientData = null
      if (client.tipo_cliente === 'Persona' && client.persona) {
        clientData = {
          id: client.persona.id,
          rut: client.persona.rut,
          nombre: client.persona.nombre,
          apellido: client.persona.apellido,
          correo: client.persona.email,
          telefono: client.persona.telefono,
          calle: client.persona.direccion,
          comuna: client.persona.comuna,
          region: client.persona.region,
        }
        this.rutQuery = client.persona.rut
      }

      this.$emit('input', this.rutQuery)
      this.$emit('client-selected', clientData)
    },

    // Crear nuevo cliente
    createNewClient() {
      this.clientSelected = true
      this.$emit('input', this.rutQuery)
      this.$emit('create-new-client', this.rutQuery)
    },

    // Limpiar selección
    clearClient() {
      this.rutQuery = ''
      this.clientSelected = false
      this.selectedCustomer = null
      this.customers = []
      this.$emit('input', '')
      this.$emit('client-cleared')
    },

    // API call para buscar clientes
    async searchClientsByRut(rut) {
      try {
        const sinDigito = rut.split('-')[0]
        // Sin filtro de client_type: si el RUT corresponde a una empresa
        // necesitamos que vuelva en la respuesta para poder detectarlo
        // (ver onRutSelected).
        const apiAddress = `api/clientes/?query=${sinDigito}`
        const user = JSON.parse(localStorage.getItem('user'))

        if (!user || !user.key) {
          console.warn('Usuario no autenticado')
          return []
        }

        const config = {
          headers: { Authorization: `Token ${user.key}` },
        }

        const response = await axios.get(apiAddress, config)
        return response.data
      } catch (error) {
        console.error('Error en búsqueda de clientes:', error)
        return []
      }
    },
  },

  beforeDestroy() {
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout)
    }
  },
}
</script>

<style lang="scss" scoped>
::v-deep .form-control {
  height: 45px;
  border: 1px solid #525252;
  border-radius: 5px;
  font-size: 15px;

  &::placeholder {
    color: #525252;
  }
}

.input-group {
  ::v-deep .form-control {
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
    border-left: none;
  }

  ::v-deep .input-group-text {
    border: 1px solid #525252;
    border-right: none;
    border-top-left-radius: 5px;
    border-bottom-left-radius: 5px;
  }
}

.selected-client-wrapper {
  background-color: #f5f6fa;
  padding: 8px 15px;
  border-radius: 8px;
  border: 1px solid #525252;
}

.selected-client {
  font-weight: 500;
  color: #23201c;
}

::v-deep .vbt-autcomplete-list {
  border: 1px solid #525252;
  border-radius: 5px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  max-height: 250px;
  overflow-y: auto;
  z-index: 1050;
  margin-top: 5px;
}

::v-deep .vbt-autcomplete-list .list-group-item {
  padding: 0.5rem 0.75rem;
  border: none;
  cursor: pointer;
}

::v-deep .vbt-autcomplete-list .list-group-item:hover,
::v-deep .vbt-autcomplete-list .list-group-item.active {
  background-color: #f8f9fa;
  color: #495057;
}

::v-deep .form-control.is-invalid {
  border-color: #dc3545;
}

.text-primary {
  color: #007bff !important;
}

small {
  font-size: 0.875rem;
}
</style>
